// @ts-nocheck
import { useRef, useEffect, useState, useMemo } from 'react';
import * as arrow from 'apache-arrow';
import useWasmParquet from 'hooks/useWasmParquet';
import { TraceLense, StreamType } from '../../types';
import { CANDLE_IDX } from 'types/impliedVol';
import { debounce } from 'lodash';
import {
  dayjs,
  fetchRawAPI,
  responseToTable,
  getTableParquetUrl,
  streamTypeForLense,
} from '../../util';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useStreamingSocket } from 'hooks/bloomberg/streaming/useStreamingSocket';

export const CANDLE_REFETCH_INTERVAL_MS = 10 * 60_000;
export const MIN_CANDLE_RERENDER_INTERVAL_MS = 1_000;

export const useFetchParquetContourData = <
  TData = arrow.Table,
  TError = unknown,
  TQueryKey extends Array<unknown> = unknown[],
>(
  {
    greek,
    intradayDate,
    intradaySym,
  }: {
    greek: TraceLense;
    intradayDate: dayjs.Dayjs;
    intradaySym: string;
  },
  queryKey?: TQueryKey,
  options?: Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'>,
) => {
  const { getWasmPromise } = useWasmParquet();
  return useQuery<TData, TError>({
    queryKey: [
      'useFetchParquetContourData',
      { greek, intradayDate, intradaySym },
    ],
    queryFn: async (): Promise<TData> => {
      const [resp, _wasm] = await Promise.all([
        fetchRawAPI(
          getTableParquetUrl(greek, intradayDate, intradaySym),
        ) as Promise<Response>,
        getWasmPromise() as Promise<unknown>,
      ]);

      if (resp.status !== 200) {
        console.error('Received status: ' + resp.status);
        throw await resp.json();
      }
      return await responseToTable(resp);
    },
    ...options,
  });
};

interface useTraceStreamingProps {
  enabled: boolean;
  intradayDate?: dayjs.Dayjs;
  hiroSym: string;
  selectedLense: string;
}
interface useTraceStreamingResult {
  timestamp2StreamedDeltaCandle: Map<number, number[] /*candle tuple*/>;
  timestamp2StreamedPrice: Map<number, number[] /*candle tuple*/>;
  rerenderIndicator: number;
}

export const useTraceStreaming = ({
  enabled,
  intradayDate,
  hiroSym,
  selectedLense,
}: useTraceStreamingProps): useTraceStreamingResult => {
  const socket = useRef(null);
  const socketRetries = useRef(0);
  const hiroStreamingRefs = {
    socket,
    socketRetries,
    currentSymbol: {
      current: hiroSym,
    },
  };

  const streamingSubscribeBitmask =
    streamTypeForLense(selectedLense, false) |
    StreamType.FilteredTotalDelta |
    StreamType.AbsolutePrice;

  const timestamp2StreamedDeltaCandle = useRef(
    new Map<number, number[] /*candle tuple*/>(),
  );

  const timestamp2StreamedPrice = useRef(
    new Map<number, number[] /*candle tuple*/>(),
  );

  const [rerenderIndicator, setRerenderIndicator] = useState(0);
  const rerenderIndicatorDebounce = useMemo(
    () =>
      debounce(
        () => setRerenderIndicator(Math.random()),
        MIN_CANDLE_RERENDER_INTERVAL_MS,
      ),
    [setRerenderIndicator],
  );
  useEffect(() => {
    return () => rerenderIndicatorDebounce.cancel();
  }, [rerenderIndicatorDebounce]);

  const updateCandleCallback = (
    extendedCandle: Array<number>,
    streamType: StreamType,
  ) => {
    const candle = extendedCandle.slice(1).map(Number);
    const timestamp = candle[CANDLE_IDX.TIMESTAMP_OPEN];
    if (!candle[CANDLE_IDX.CLOSE]) {
      return;
    }
    if (streamType == StreamType.FilteredTotalDelta) {
      timestamp2StreamedDeltaCandle.current?.set(timestamp, candle);
    } else if (streamType == StreamType.AbsolutePrice) {
      timestamp2StreamedPrice.current.set(timestamp, candle);
    }
    rerenderIndicatorDebounce();
  };

  // prune old candles
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const interval = setInterval(() => {
      const candleAgeTimestampLimit = dayjs()
        .subtract(CANDLE_REFETCH_INTERVAL_MS * 2, 'milliseconds')
        .valueOf();
      for (const m of [
        timestamp2StreamedDeltaCandle.current,
        timestamp2StreamedPrice.current,
      ]) {
        for (const k of m.keys()) {
          if (k < candleAgeTimestampLimit) {
            m.delete(k);
          }
        }
      }
    }, CANDLE_REFETCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [timestamp2StreamedDeltaCandle, timestamp2StreamedPrice, enabled]);

  const { reconnectSocket } = useStreamingSocket({
    enabled,
    hiroStreamingRefs,
    updateCandleCallback,
    streamingSubscribeBitmask,
    endDate: intradayDate ?? dayjs(),
  });
  useEffect(
    () => {
      if (enabled) {
        reconnectSocket();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled, hiroSym],
  );

  return {
    timestamp2StreamedDeltaCandle: timestamp2StreamedDeltaCandle.current,
    timestamp2StreamedPrice: timestamp2StreamedPrice.current,
    rerenderIndicator: rerenderIndicator,
  };
};
